(() => {
  var __defProp = Object.defineProperty;
  var __typeError = (msg) => {
    throw TypeError(msg);
  };
  var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
  var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
  var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
  var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
  var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
  var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

  // src/scripts/_base/utilities.js
  var requestIdleCallback = typeof window.requestIdleCallback == "function" ? window.requestIdleCallback : setTimeout;
  var viewTransition = {
    current: void 0
  };
  var reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  var mediaQueryLarge = matchMedia("(min-width: 750px)");
  var _queue, _scheduled;
  var Scheduler = class {
    constructor() {
      /** @type {Set<() => void>} */
      __privateAdd(this, _queue, /* @__PURE__ */ new Set());
      /** @type {boolean} */
      __privateAdd(this, _scheduled, false);
      /** @param {() => void} task */
      __publicField(this, "schedule", async (task) => {
        __privateGet(this, _queue).add(task);
        if (!__privateGet(this, _scheduled)) {
          __privateSet(this, _scheduled, true);
          if (viewTransition.current) await viewTransition.current;
          requestAnimationFrame(this.flush);
        }
      });
      __publicField(this, "flush", () => {
        for (const task of __privateGet(this, _queue)) {
          setTimeout(task, 0);
        }
        __privateGet(this, _queue).clear();
        __privateSet(this, _scheduled, false);
      });
    }
  };
  _queue = new WeakMap();
  _scheduled = new WeakMap();
  var scheduler = new Scheduler();
  if (typeof Theme !== "undefined") {
    Theme.utilities = {
      ...Theme.utilities,
      scheduler
    };
  }

  // src/scripts/_base/component.js
  var DeclarativeShadowElement = class extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        const template = this.querySelector(':scope > template[shadowrootmode="open"]');
        if (!(template instanceof HTMLTemplateElement)) return;
        const shadow = this.attachShadow({ mode: "open" });
        shadow.append(template.content.cloneNode(true));
      }
    }
  };
  var _Component_instances, updateRefs_fn, _mutationObserver, _isDescendant;
  var Component = class extends DeclarativeShadowElement {
    constructor() {
      super(...arguments);
      __privateAdd(this, _Component_instances);
      /**
       * An object holding references to child elements with `ref` attributes.
       *
       * @type {RefsType<T>}
       */
      __publicField(
        this,
        "refs",
        /** @type {RefsType<T>} */
        {}
      );
      /**
       * An array of required refs. If a ref is not found, an error will be thrown.
       *
       * @type {string[] | undefined}
       */
      __publicField(this, "requiredRefs");
      /**
       * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
       *
       * @type {MutationObserver}
       */
      __privateAdd(this, _mutationObserver, new MutationObserver((mutations) => {
        if (mutations.some(
          (m) => m.type === "attributes" && __privateGet(this, _isDescendant).call(this, m.target) || m.type === "childList" && [...m.addedNodes, ...m.removedNodes].some(__privateGet(this, _isDescendant))
        )) {
          __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
        }
      }));
      /**
       * Checks if a given node is a descendant of this component.
       *
       * @param {Node} node - The node to check.
       * @returns {boolean} True if the node is a descendant of this component.
       */
      __privateAdd(this, _isDescendant, (node) => getClosestComponent(getAncestor(node)) === this);
    }
    /**
     * Gets the root node of the component, which is either its shadow root or the component itself.
     *
     * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
     */
    get roots() {
      return this.shadowRoot ? [this, this.shadowRoot] : [this];
    }
    /**
     * Called when the element is connected to the document's DOM.
     *
     * Initializes event listeners and refs.
     */
    connectedCallback() {
      super.connectedCallback();
      registerEventListeners();
      __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
      this.loggerColor = "#5983BD";
      requestIdleCallback(() => {
        for (const root of this.roots) {
          __privateGet(this, _mutationObserver).observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["ref"],
            attributeOldValue: true
          });
        }
      });
    }
    /**
     * Called when the element is re-rendered by the Section Rendering API.
     */
    updatedCallback() {
      __privateGet(this, _mutationObserver).takeRecords();
      __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
    }
    /**
     * Called when the element is disconnected from the document's DOM.
     *
     * Disconnects the mutation observer.
     */
    disconnectedCallback() {
      __privateGet(this, _mutationObserver).disconnect();
    }
    /**
     * Prints the given arguments if the component is currently in debug mode;
     * @param  {...any} args 
     */
    log(...args) {
      if (this.debug) {
        console.log(`%c[${this.tagName}]`, `color: ${this.loggerColor}`, ...args);
      }
    }
  };
  _Component_instances = new WeakSet();
  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  updateRefs_fn = function() {
    var _a, _b;
    const refs = (
      /** @type any */
      {}
    );
    const elements = this.roots.reduce(
      (acc, root) => {
        for (const element of root.querySelectorAll("[ref]")) {
          if (!__privateGet(this, _isDescendant).call(this, element)) continue;
          acc.add(element);
        }
        return acc;
      },
      /** @type {Set<Element>} */
      /* @__PURE__ */ new Set()
    );
    for (const ref of elements) {
      const refName = (_a = ref.getAttribute("ref")) != null ? _a : "";
      const isArray = refName.endsWith("[]");
      const path = isArray ? refName.slice(0, -2) : refName;
      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];
        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }
    if ((_b = this.requiredRefs) == null ? void 0 : _b.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }
    this.refs = /** @type {RefsType<T>} */
    refs;
  };
  _mutationObserver = new WeakMap();
  _isDescendant = new WeakMap();
  function getAncestor(node) {
    if (node.parentNode) return node.parentNode;
    const root = node.getRootNode();
    if (root instanceof ShadowRoot) return root.host;
    return null;
  }
  function getClosestComponent(node) {
    if (!node) return null;
    if (node instanceof Component) return node;
    if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith("-component")) return node;
    const ancestor = getAncestor(node);
    if (ancestor) return getClosestComponent(ancestor);
    return null;
  }
  var initialized = false;
  function registerEventListeners() {
    if (initialized) return;
    initialized = true;
    const events = ["click", "change", "select", "focus", "blur", "submit", "input", "keydown", "keyup", "toggle"];
    const shouldBubble = ["focus", "blur"];
    const expensiveEvents = ["pointerenter", "pointerleave"];
    for (const eventName of [...events, ...expensiveEvents]) {
      const attribute = `on:${eventName}`;
      document.addEventListener(
        eventName,
        (event) => {
          var _a;
          const element = getElement(event);
          if (!element) return;
          const proxiedEvent = event.target !== element ? new Proxy(event, {
            get(target, property) {
              if (property === "target") return element;
              const value2 = Reflect.get(target, property);
              if (typeof value2 === "function") {
                return value2.bind(target);
              }
              return value2;
            }
          }) : event;
          const value = (_a = element.getAttribute(attribute)) != null ? _a : "";
          let [selector, method] = value.split("/");
          const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
          const data = matches ? matches[2] : null;
          const instance = selector ? selector.startsWith("#") ? document.querySelector(selector) : element.closest(selector) : getClosestComponent(element);
          if (!(instance instanceof Component) || !method) return;
          method = method.replace(/\?.*/, "");
          const callback = (
            /** @type {any} */
            instance[method]
          );
          if (typeof callback === "function") {
            try {
              const args = [proxiedEvent];
              if (data) args.unshift(parseData(data));
              callback.call(instance, ...args);
            } catch (error) {
              console.error(error);
            }
          }
        },
        { capture: true }
      );
    }
    function getElement(event) {
      var _a, _b;
      const target = (_b = (_a = event.composedPath) == null ? void 0 : _a.call(event)[0]) != null ? _b : event.target;
      if (!(target instanceof Element)) return;
      if (target.hasAttribute(`on:${event.type}`)) {
        return target;
      }
      if (expensiveEvents.includes(event.type)) {
        return null;
      }
      return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
    }
  }
  function parseData(str) {
    const delimiter = str[0];
    const data = str.slice(1);
    return delimiter === "?" ? Object.fromEntries(
      Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
    ) : parseValue(data);
  }
  function parseValue(str) {
    if (str === "true") return true;
    if (str === "false") return false;
    const maybeNumber = Number(str);
    if (!isNaN(maybeNumber) && str.trim() !== "") return maybeNumber;
    return str;
  }
  var MissingRefError = class extends Error {
    /**
     * @param {string} ref
     * @param {Component} component
     */
    constructor(ref, component) {
      super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
    }
  };

  // src/scripts/_base/events.js
  var ThemeEvents = class {
  };
  /** @static @constant {string} Event triggered when a variant is selected */
  __publicField(ThemeEvents, "variantSelected", "variant:selected");
  /** @static @constant {string} Event triggered when a variant is changed */
  __publicField(ThemeEvents, "variantUpdate", "variant:update");
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  __publicField(ThemeEvents, "cartUpdate", "cart:update");
  /** @static @constant {string} Event triggered when a cart update fails */
  __publicField(ThemeEvents, "cartError", "cart:error");
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  __publicField(ThemeEvents, "mediaStartedPlaying", "media:started-playing");
  // Event triggered when quantity-selector value is changed
  __publicField(ThemeEvents, "quantitySelectorUpdate", "quantity-selector:update");
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  __publicField(ThemeEvents, "megaMenuHover", "megaMenu:hover");
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  __publicField(ThemeEvents, "zoomMediaSelected", "zoom-media:selected");
  /** @static @constant {string} Event triggered when a discount is applied */
  __publicField(ThemeEvents, "discountUpdate", "discount:update");
  /** @static @constant {string} Event triggered when changing collection filters */
  __publicField(ThemeEvents, "FilterUpdate", "filter:update");
  var _CartAddEvent = class _CartAddEvent extends Event {
    /**
     * Creates a new CartAddEvent
     * @param {Object} [resource] - The new cart object
     * @param {Object} [data] - Additional event data
     * @param {boolean} [data.didError] - Whether the cart operation failed
     * @param {string} [data.source] - The source of the cart update
     * @param {string} [data.productId] - The id of the product card that was updated
     * @param {string} [data.variantId] - The id of the product variant that was added
     * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
     */
    constructor(resource, data) {
      super(_CartAddEvent.eventName, { bubbles: true });
      this.detail = {
        resource,
        data: {
          ...data
        }
      };
    }
  };
  __publicField(_CartAddEvent, "eventName", ThemeEvents.cartUpdate);
  var CartAddEvent = _CartAddEvent;

  // src/scripts/product/components/product-form.js
  var _cartDrawer;
  var ProductForm = class extends Component {
    constructor() {
      super();
      __privateAdd(this, _cartDrawer, document.querySelector("cart-drawer-component"));
      this.debug = false;
      this.loggerColor = "#777CF7";
      this.adding = false;
      this.form = this.querySelector("form");
    }
    connectedCallback() {
      this.log("connected");
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
    }
    async handleSubmit(event) {
      var _a, _b, _c, _d, _e;
      event.preventDefault();
      if (this.adding) return;
      this.adding = true;
      const variantId = parseInt(
        ((_a = this.querySelector("[data-main-variant-id]")) == null ? void 0 : _a.value) || 0
      );
      const quantity = parseInt(
        ((_b = this.querySelector("[data-quantity]")) == null ? void 0 : _b.value) || 1
      );
      const sellingPlan = ((_c = this.querySelector("[data-selling-plan-id]")) == null ? void 0 : _c.value) || false;
      const variantPrice = parseInt(
        (_d = this.querySelector("[data-variant-price]")) == null ? void 0 : _d.value
      );
      const addOns = this.querySelectorAll("[data-addon-variant-id") || false;
      const { cartWelcomeKit } = ((_e = document.querySelector("[data-cart-welcome-kit]")) == null ? void 0 : _e.dataset.cartWelcomeKit) === "true";
      let progressGiftUpdates = {};
      this.log("handleSubmit:", variantId, quantity, sellingPlan);
      if (!variantId) {
        this.log("no variantId found");
        return;
      }
      const productSubmitBtn = this.querySelector('button[type="submit"]');
      productSubmitBtn == null ? void 0 : productSubmitBtn.classList.add("loading");
      const properties = {};
      const { prop, propValue } = this.querySelector("[data-main-variant-id]").dataset;
      if (prop) {
        properties[`${prop}`] = propValue;
      }
      const { dialog, cartItems } = __privateGet(this, _cartDrawer).refs;
      if (dialog && dialog.classList.contains("open") && cartItems && !isNaN(variantPrice)) {
        const progressUpdate = variantPrice * quantity;
        progressGiftUpdates = cartItems.triggerProgressAnimation(progressUpdate);
      }
      let formData = {
        items: [
          {
            id: variantId,
            quantity,
            selling_plan: sellingPlan ? sellingPlan : null,
            properties
          }
        ]
      };
      if (addOns && !cartWelcomeKit) {
        addOns.forEach((addOn) => {
          const addOnVariant = parseInt(addOn.value);
          const { prop: prop2, propValue: propValue2 } = addOn.dataset;
          const item = {
            id: addOnVariant,
            quantity: 1,
            properties: {}
          };
          if (prop2) {
            item.properties[prop2] = propValue2;
          }
          if (properties["_welcome_kit"] === "true") {
            item.properties["_welcome_gift"] = true;
          }
          if (!isNaN(addOnVariant)) {
            formData.items.push(item);
          }
        });
      }
      if (progressGiftUpdates.addGift) {
        formData.items.push({
          id: progressGiftUpdates.addGift,
          quantity: 1,
          properties: {
            _progress_gift: progressGiftUpdates.promo ? progressGiftUpdates.promo : "true"
          }
        });
      }
      if (__privateGet(this, _cartDrawer)) {
        formData.sections = __privateGet(this, _cartDrawer).getSectionsToRender();
        formData.sections_url = "/cart";
      }
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData)
        });
        const data = await response.json();
        this.dispatchEvent(
          new CartAddEvent(data, {
            source: "product-form",
            sections: data.sections
          })
        );
        this.adding = false;
      } catch (e) {
        console.error("Error:", e);
      } finally {
        productSubmitBtn == null ? void 0 : productSubmitBtn.classList.remove("loading");
      }
    }
  };
  _cartDrawer = new WeakMap();
  function defineProductForm() {
    if (!customElements.get("product-form-component")) {
      customElements.define("product-form-component", ProductForm);
    }
  }

  // src/scripts/product/product-default.js
  defineProductForm();
})();
